/**
 * كتالوج إدارة المواهب وشراكة الموارد البشرية.
 *
 * جلسةٌ تُعقد فيوضَع فيها كلُّ من يُتحدَّث عنه في مصفوفةِ الأداء
 * والإمكانات، ويُكتب لكلٍّ ما يُفعل به. ثمّ شريكٌ لكلّ وحدةٍ يُسأل عنها.
 *
 * ## ولماذا محوران لا محور
 *
 * الأداءُ والإمكاناتُ شيئان: من يؤدّي اليوم ممتازًا في عمله قد لا يصلح
 * لما فوقه، ومن يؤدّي وسطًا قد يكون في غير موضعه. وخلطُهما هو خطأُ
 * مراجعات المواهب الأشهر — وثمنُه ترقيةُ من كان يُحسن عملَه إلى عملٍ
 * يُسيئه.
 *
 * ## وما لا يفعله هذا الباب
 *
 * لا يرفع أحدًا ولا يخفضه. الجلسةُ تُنتج قرارًا مكتوبًا في حقّ كلّ اسم،
 * ومن يملك القرارَ يفعله في بابه: الترقيةُ في ملفّ الموظّف، والتدريبُ في
 * التدريب، والخلافةُ في خطط الإحلال.
 */
import type { Labelled } from './common.js';

/* ==========================================================================
   المحور الثاني: الإمكانات
   ========================================================================== */

export const POTENTIALS = ['HIGH', 'MEDIUM', 'LOW'] as const;
export type Potential = (typeof POTENTIALS)[number];

export interface PotentialDef extends Labelled<Potential> {
  description: string;
  /** ترتيبُ العرض في المصفوفة — الأعلى أوّلًا. */
  order: number;
}

export const POTENTIAL_DEFS: readonly PotentialDef[] = [
  {
    key: 'HIGH',
    label: 'إمكاناتٌ عالية',
    kind: 'success',
    description: 'يصلح لمستوًى أعلى خلال سنةٍ أو سنتين.',
    order: 1,
  },
  {
    key: 'MEDIUM',
    label: 'إمكاناتٌ متوسّطة',
    kind: 'info',
    description: 'ينمو في مستواه ويتوسّع فيه.',
    order: 2,
  },
  {
    key: 'LOW',
    label: 'في موضعه',
    kind: 'neutral',
    description:
      'هذا موضعُه ويُحسنه — وليست كلمةَ ذمّ: أكثرُ الشركة هنا وبه تقوم.',
    order: 3,
  },
] as const;

export const POTENTIAL_MAP = Object.fromEntries(
  POTENTIAL_DEFS.map((d) => [d.key, d]),
) as Readonly<Record<Potential, PotentialDef>>;

/* ==========================================================================
   احتمالُ الترك
   ========================================================================== */

export const FLIGHT_RISKS = ['LOW', 'MEDIUM', 'HIGH'] as const;
export type Risk = (typeof FLIGHT_RISKS)[number];

export interface RiskDef extends Labelled<Risk> {
  description: string;
}

export const RISK_DEFS: readonly RiskDef[] = [
  {
    key: 'LOW',
    label: 'منخفض',
    kind: 'success',
    description: 'لا ما يدلّ على أنّه يفكّر في الترك.',
  },
  {
    key: 'MEDIUM',
    label: 'متوسّط',
    kind: 'warning',
    description: 'مؤشّراتٌ تحتاج حديثًا معه.',
  },
  {
    key: 'HIGH',
    label: 'مرتفع',
    kind: 'danger',
    description: 'يُرجَّح أن يترك خلال سنة — وهذا تقديرٌ مكتوبٌ لا قياس.',
  },
] as const;

export const RISK_MAP = Object.fromEntries(
  RISK_DEFS.map((d) => [d.key, d]),
) as Readonly<Record<Risk, RiskDef>>;

/* ==========================================================================
   المصفوفة
   ========================================================================== */

/** مراتبُ الأداء كما تُكتب في التقييم — المحورُ الأوّل. */
export const TALENT_PERFORMANCE = ['TOP', 'STRONG', 'MEETS', 'BELOW'] as const;
export type TalentPerformance = (typeof TALENT_PERFORMANCE)[number];

export interface BoxDef {
  key: string;
  label: string;
  kind: 'success' | 'info' | 'warning' | 'danger' | 'neutral';
  /** ما يُفعل بمن وقع هنا — يُقترح في الشاشة ولا يُفرض. */
  action: string;
}

/**
 * خانةُ المصفوفة من المحورين.
 *
 * والخاناتُ سبعٌ لا تسع: «دون المطلوب» تُجمع في واحدةٍ مهما كانت
 * الإمكانات — من لا يؤدّي اليوم لا يُخطَّط لما فوقه قبل أن يؤدّي. وتسعُ
 * خاناتٍ تُنتج ثلاثةَ أسماءٍ لحالةٍ واحدة، ويقف أمامها من يقرأ.
 */
export function boxOf(performance: string, potential: Potential): BoxDef {
  if (performance === 'BELOW') {
    return {
      key: 'UNDER',
      label: 'دون المطلوب',
      kind: 'danger',
      action:
        'خطّةُ تحسينِ أداءٍ بمدّةٍ وأهدافٍ مكتوبة — قبل أيّ حديثٍ عن نموّ.',
    };
  }

  if (potential === 'HIGH') {
    if (performance === 'TOP') {
      return {
        key: 'STAR',
        label: 'نجمٌ',
        kind: 'success',
        action:
          'يُحتفَظ به ويُعدّ لما فوقه: خلافةٌ مكتوبة، وبرنامجُ قيادة، وحديثٌ صريحٌ عن مساره.',
      };
    }
    return {
      key: 'RISER',
      label: 'صاعدٌ',
      kind: 'success',
      action: 'يُستثمر فيه: تدريبٌ ومهامٌّ أكبرُ ممّا يفعل اليوم.',
    };
  }

  if (potential === 'MEDIUM') {
    if (performance === 'TOP') {
      return {
        key: 'PILLAR',
        label: 'ركنٌ',
        kind: 'info',
        action:
          'يُكافَأ ويُوسَّع دورُه في مستواه — لا كلُّ متميّزٍ يريد أن يُرقَّى.',
      };
    }
    return {
      key: 'CORE',
      label: 'قلبُ الفريق',
      kind: 'info',
      action: 'يُبقى ويُنمّى في موضعه — وهؤلاء أكثرُ الشركة.',
    };
  }

  if (performance === 'TOP') {
    return {
      key: 'EXPERT',
      label: 'خبيرٌ في موضعه',
      kind: 'success',
      action: 'يُحتفَظ به حيث هو: نقلُه إلى إدارةٍ يخسره ويخسر عملَه.',
    };
  }
  return {
    key: 'STEADY',
    label: 'ثابتٌ',
    kind: 'neutral',
    action: 'يؤدّي ما عليه — ويُترك في موضعه.',
  };
}

/** خاناتُ المصفوفة كلُّها — تُرسم منها الشبكة. */
export const BOX_KEYS = [
  'STAR',
  'RISER',
  'PILLAR',
  'CORE',
  'EXPERT',
  'STEADY',
  'UNDER',
] as const;

/* ==========================================================================
   حالةُ الجلسة
   ========================================================================== */

export const TALENT_STATUSES = ['DRAFT', 'FINAL'] as const;
export type TalentStatus = (typeof TALENT_STATUSES)[number];

export interface TalentStatusDef extends Labelled<TalentStatus> {
  description: string;
  writable: boolean;
}

export const TALENT_STATUS_DEFS: readonly TalentStatusDef[] = [
  {
    key: 'DRAFT',
    label: 'تُناقَش',
    kind: 'info',
    description: 'تُكتب وتُعدَّل — ولا تُقرأ حكمًا بعد.',
    writable: true,
  },
  {
    key: 'FINAL',
    label: 'اعتُمدت',
    kind: 'success',
    description: 'ما فيها قرارُ الجلسة — ولا يُعدَّل بعدها.',
    writable: false,
  },
] as const;

export const TALENT_STATUS_MAP = Object.fromEntries(
  TALENT_STATUS_DEFS.map((d) => [d.key, d]),
) as Readonly<Record<TalentStatus, TalentStatusDef>>;

/** أثرُ فقدِه: من ثلاثٍ، وواحدٌ أشدُّها. */
export const IMPACT_LABELS: Readonly<Record<number, string>> = {
  1: 'يُعطّل عملًا قائمًا',
  2: 'يُشعَر به ويُعوَّض',
  3: 'يُعوَّض بلا أثر',
};

/**
 * من يُقال عنه «في خطر».
 *
 * إمكاناتٌ عالية، واحتمالُ تركٍ مرتفع، وفقدُه يُعطّل — الثلاثةُ معًا. وكلٌّ
 * منها وحدَه لا يكفي: من إمكاناتُه عاليةٌ وباقٍ لا يحتاج تدخّلًا اليوم.
 */
export function atRisk(input: {
  potential: Potential;
  flightRisk: Risk;
  impactOfLoss: number;
}): boolean {
  return (
    input.potential === 'HIGH' &&
    input.flightRisk === 'HIGH' &&
    input.impactOfLoss <= 2
  );
}

/* ==========================================================================
   تغطيةُ الشراكة
   ========================================================================== */

/**
 * ما يُقاس في تغطية الشركاء.
 *
 * وحدةٌ بلا شريكٍ لا يُسأل عنها أحد، وشريكٌ على عشر وحداتٍ لا يعرف أيًّا
 * منها. والرقمان معًا هما الخبر، ولا يُفرض حدٌّ أعلى في الشيفرة: شركةٌ من
 * ثلاثين وحدةً ومرشدٍ واحدٍ تعرف حالَها.
 */
export interface PartnerLoad {
  partnerId: string;
  name: string;
  units: number;
  headcount: number;
}
